/**
 * ItensContagemColumnTooltip - Tooltip showing expected column info for Excel imports
 */
import React, { useState } from 'react';
import { Info } from 'lucide-react';

export const ORDEM_COLUMNS = [
  { col: 'A', name: 'Código Cliente', required: true },
  { col: 'B', name: 'Regime Especial', required: false },
  { col: 'C', name: 'Código', required: true },
  { col: 'D', name: 'Descrição', required: false },
  { col: 'E', name: 'Lote', required: true },
  { col: 'F', name: 'Validade', required: true },
  { col: 'G', name: 'Qtde (Sistema)', required: true },
  { col: 'H', name: 'Coletar', required: false },
];

export const RESULTADO_COLUMNS = [
  { col: 'A', name: 'Código Cliente', required: true },
  { col: 'B', name: 'Código', required: true },
  { col: 'C', name: 'Descrição', required: false },
  { col: 'D', name: 'Lote', required: true },
  { col: 'E', name: 'Validade', required: true },
  { col: 'F', name: 'Qtde (Contada)', required: true },
];

type ColumnDef = { col: string; name: string; required: boolean };

interface ColumnTooltipProps {
  columns: ColumnDef[];
  color: 'emerald' | 'blue';
  position?: 'left' | 'right';
}

export function ColumnTooltip({ columns, color, position = 'right' }: ColumnTooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const bgColor = color === 'emerald' ? 'bg-emerald-50 dark:bg-emerald-900/90' : 'bg-blue-50 dark:bg-blue-900/90';
  const borderColor = color === 'emerald' ? 'border-emerald-300 dark:border-emerald-600' : 'border-blue-300 dark:border-blue-600';
  const iconColor = color === 'emerald' ? 'text-emerald-600 dark:text-emerald-400' : 'text-blue-600 dark:text-blue-400';
  const positionClass = position === 'left' ? 'right-0' : 'left-0';

  return (
    <div className="relative inline-block">
      <button
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        className={`p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition ${iconColor}`}
        title="Ver colunas esperadas"
      >
        <Info size={18} />
      </button>
      {isVisible && (
        <div className={`absolute z-50 ${positionClass} top-full mt-2 w-64 ${bgColor} ${borderColor} border rounded-lg shadow-xl p-3`}>
          <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mb-2">
            Colunas esperadas:
          </div>
          <div className="space-y-1">
            {columns.map((col) => (
              <div key={col.col} className="flex items-center text-xs text-gray-600 dark:text-gray-300">
                <span className="font-mono bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded mr-2 min-w-[20px] text-center">
                  {col.col}
                </span>
                <span className={col.required ? 'font-medium' : ''}>
                  {col.name}
                  {col.required && <span className="text-red-500 ml-0.5">*</span>}
                </span>
              </div>
            ))}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
            * Campos obrigatórios
          </div>
          <div className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium">
            Ao importar, os dados anteriores serão substituídos.
          </div>
        </div>
      )}
    </div>
  );
}
